/**
 * /app/slots — list-first redesign (PULSE-XX, 2026-05-17 rev 3).
 *
 * Why not a matrix any more
 *   Discovery covers ~20 of ~38 logical slots. The 5×3×3 matrix was
 *   90% empty cells; the signal got lost in the dashes. Now we lead
 *   with a list of WHAT IS ACTUALLY TRADING and put the coverage map
 *   at the bottom as small context.
 *
 * Layout (top-down):
 *   1. Header — title + summary counts + active-only filter
 *   2. Filter bar — regime · signal_type · search
 *   3. Active strategies (sortable table) — one row per logical slot
 *      with backtest + live metrics
 *   4. Coverage heatmap — compact grid showing which slots discovery
 *      has filled vs left empty (so it's obvious WHY some are missing)
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Drawer } from '@/components/ui/Drawer'
import {
  type SlotRow,
  useSlotDetail,
  useSlots,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatUsd, timeAgo } from '@/lib/format'

const SIGNAL_TYPES = [
  'pump_dump', 'super_pump_dump', 'divergence', 'reversal', 'liquidation',
] as const
const REGIMES = ['bullish', 'consolidation', 'bearish'] as const
const TFS = ['15m', '1h', '4h'] as const
const ML_ENABLED = false

type Regime = (typeof REGIMES)[number]
type SortBy = 'signal' | 'pnl_24h' | 'pnl_7d' | 'live_wr' | 'bt_compound' | 'rank'

/** One row per logical slot, aggregated across user accounts. */
type LogicalSlot = {
  key: string
  signal_type: string
  timeframe: string
  regime_bucket: string | null
  direction: string | null
  is_active: boolean
  slot_state: string | null
  // Active strategy snapshot
  strategy_id: number | null
  strategy_name: string | null
  strategy_tp_pct: number | null
  strategy_sl_pct: number | null
  strategy_hold_min: number | null
  backtest_pnl_pct: number | null
  backtest_wr: number | null
  // A/B identity
  strategy_a_id: number | null
  strategy_b_id: number | null
  // Discovery provenance
  discovery_rank: number | null
  discovery_run_id: number | null
  discovery_weighted_pnl: number | null
  bt_compound_30d_pct: number | null
  bt_trades_30d: number | null
  bt_compound_cycles: number | null
  // Live PnL aggregated across covered users
  pnl_24h: number
  trades_24h: number
  pnl_7d: number
  trades_7d: number
  // Metadata
  user_count: number
  pair_ids: number[]
}

/* ─── Aggregation ──────────────────────────────────────────────────── */

function aggregateLogical(slots: SlotRow[]): LogicalSlot[] {
  const byKey = new Map<string, LogicalSlot>()
  for (const s of slots) {
    const key = `${s.signal_type}|${s.timeframe}|${s.regime_bucket ?? ''}|${s.direction ?? ''}`
    let g = byKey.get(key)
    if (!g) {
      g = {
        key,
        signal_type: s.signal_type,
        timeframe: s.timeframe,
        regime_bucket: s.regime_bucket,
        direction: s.direction,
        is_active: s.is_active,
        slot_state: s.slot_state,
        strategy_id: s.strategy_id,
        strategy_name: s.strategy_name,
        strategy_tp_pct: s.strategy_tp_pct,
        strategy_sl_pct: s.strategy_sl_pct,
        strategy_hold_min: s.strategy_hold_min,
        backtest_pnl_pct: s.strategy_backtest_pnl_pct,
        backtest_wr: s.strategy_backtest_wr,
        strategy_a_id: s.strategy_a_id,
        strategy_b_id: s.strategy_b_id,
        discovery_rank: s.discovery_rank,
        discovery_run_id: s.discovery_run_id,
        discovery_weighted_pnl: s.discovery_weighted_pnl,
        bt_compound_30d_pct: s.bt_compound_30d_pct,
        bt_trades_30d: s.bt_trades_30d,
        bt_compound_cycles: s.bt_compound_cycles,
        pnl_24h: 0,
        trades_24h: 0,
        pnl_7d: 0,
        trades_7d: 0,
        user_count: 0,
        pair_ids: [],
      }
      byKey.set(key, g)
    }
    g.is_active = g.is_active || s.is_active
    g.slot_state = pickBestState(g.slot_state, s.slot_state)
    g.pnl_24h += s.real_pnl_24h ?? 0
    g.trades_24h += s.real_trades_24h ?? 0
    g.pnl_7d += s.real_pnl_7d ?? 0
    g.trades_7d += s.real_trades_7d ?? 0
    g.pair_ids.push(s.id)
    g.user_count += 1
  }
  return Array.from(byKey.values())
}

function pickBestState(a: string | null, b: string | null): string | null {
  const order = ['active', 'probing', 'reworking', 'weak', 'hibernating', 'disabled', 'inactive']
  if (!a) return b
  if (!b) return a
  const ai = order.indexOf(a), bi = order.indexOf(b)
  if (ai === -1) return b
  if (bi === -1) return a
  return ai < bi ? a : b
}

/* ─── Page ─────────────────────────────────────────────────────────── */

export default function Slots() {
  const { t } = useTranslation('app')
  const [activeOnly, setActiveOnly] = useState(false)
  const [regimeFilter, setRegimeFilter] = useState<Regime | 'all'>('all')
  const [signalFilter, setSignalFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortBy>('pnl_7d')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [detailId, setDetailId] = useState<number | null>(null)

  const { data, isLoading } = useSlots({ active_only: activeOnly || undefined })

  const allLogical = useMemo(() => {
    const all = aggregateLogical(data?.items ?? [])
    // PULSE-50: ML predictor is off. Drop ml-direction slots from the
    // main list — they will never trade. Coverage heatmap also skips
    // ml below.
    return ML_ENABLED ? all : all.filter((s) => s.direction !== 'ml')
  }, [data])

  // Filtered view (regime + signal_type)
  const filtered = useMemo(() => {
    let arr = allLogical
    if (regimeFilter !== 'all') {
      arr = arr.filter((s) => (s.regime_bucket ?? null) === regimeFilter)
    }
    if (signalFilter !== 'all') {
      arr = arr.filter((s) => s.signal_type === signalFilter)
    }
    return [...arr].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      switch (sortBy) {
        case 'pnl_24h':       return dir * (a.pnl_24h - b.pnl_24h)
        case 'pnl_7d':        return dir * (a.pnl_7d - b.pnl_7d)
        case 'live_wr':       return dir * (_liveWr(a) - _liveWr(b))
        case 'bt_compound':   return dir * ((a.bt_compound_30d_pct ?? -100) - (b.bt_compound_30d_pct ?? -100))
        case 'rank':          return dir * ((a.discovery_rank ?? 999) - (b.discovery_rank ?? 999))
        case 'signal':
        default:
          return dir * a.key.localeCompare(b.key)
      }
    })
  }, [allLogical, regimeFilter, signalFilter, sortBy, sortDir])

  // Summary chips
  const totalActive = allLogical.filter((s) => s.is_active).length
  const totalLogical = allLogical.length
  const livePnl24h = allLogical.reduce((acc, s) => acc + s.pnl_24h, 0)
  const liveTrades24h = allLogical.reduce((acc, s) => acc + s.trades_24h, 0)
  const accountCount = new Set((data?.items ?? []).map((s) => s.user_id)).size

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
      {/* ─── Header ──────────────────────────────────────────────── */}
      <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('slots.title')}
          </h1>
          <p className="text-sm text-textDim mt-1">
            {t('slots.subtitle_v3', {
              defaultValue: 'Стратегии, активные сейчас в торговле. Каждая строка — один слот, агрегированный по аккаунтам.',
            })}
          </p>
        </div>
      </header>

      {/* ─── Summary tiles ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryTile
          label={t('slots.tile.active', { defaultValue: 'Активных слотов' })}
          value={`${totalActive} / ${totalLogical}`}
          hint={t('slots.tile.active_hint', {
            users: accountCount,
            defaultValue: `по {{users}} аккаунтам`,
          })}
        />
        <SummaryTile
          label={t('slots.tile.pnl_24h', { defaultValue: 'PnL · 24ч' })}
          value={liveTrades24h > 0 ? formatUsd(livePnl24h, { sign: true }) : '—'}
          tone={liveTrades24h === 0 ? 'dim' : livePnl24h >= 0 ? 'good' : 'bad'}
          hint={liveTrades24h > 0 ? `${liveTrades24h} сделок` : 'нет сделок'}
        />
        <SummaryTile
          label={t('slots.tile.coverage', { defaultValue: 'Покрытие' })}
          value={`${Math.round((totalLogical / 90) * 100)}%`}
          hint={`${totalLogical} из 90 возможных`}
        />
        <SummaryTile
          label={t('slots.tile.empty_regimes', { defaultValue: 'Пустые направления' })}
          value={String(
            REGIMES.filter((r) => allLogical.filter((s) => s.regime_bucket === r).length === 0).length,
          )}
          hint="discovery ничего не нашёл"
        />
      </div>

      {/* ─── Filter bar ──────────────────────────────────────────── */}
      <div className="bg-ink border border-line px-4 py-3 mb-3 flex items-center gap-4 flex-wrap text-xs">
        <div className="flex items-center gap-2">
          <span className="text-textDim">{t('slots.filter.regime', { defaultValue: 'Фаза:' })}</span>
          {(['all', ...REGIMES] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRegimeFilter(r)}
              className={cn(
                'px-2.5 py-1 rounded transition-colors',
                regimeFilter === r
                  ? 'bg-fire-faint text-fire border border-fire/40'
                  : 'text-textDim hover:text-text border border-transparent',
              )}
            >
              {r === 'all'
                ? t('slots.filter.all', { defaultValue: 'Все' })
                : t(`dashboard.market.phase.${r}`, { defaultValue: r })}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className="text-textDim">{t('slots.filter.signal', { defaultValue: 'Сигнал:' })}</span>
          <select
            value={signalFilter}
            onChange={(e) => setSignalFilter(e.target.value)}
            className="bg-elevated border border-line text-text px-2 py-1 rounded font-mono text-xs"
          >
            <option value="all">{t('slots.filter.all', { defaultValue: 'Все' })}</option>
            {SIGNAL_TYPES.map((sig) => (
              <option key={sig} value={sig}>
                {t(`signal_type.${sig}`, { defaultValue: sig })}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-textDim ml-auto">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="accent-fire"
          />
          {t('slots.filter.active_only')}
        </label>
      </div>

      {/* ─── Strategy list (the main thing) ───────────────────── */}
      {isLoading || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">—</div>
      ) : filtered.length === 0 ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
          {t('slots.no_match', { defaultValue: 'Нет слотов по этим фильтрам.' })}
        </div>
      ) : (
        <StrategyTable
          rows={filtered}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={(b) => {
            if (b === sortBy) setSortDir(sortDir === 'desc' ? 'asc' : 'desc')
            else { setSortBy(b); setSortDir('desc') }
          }}
          onPick={(s) => s.pair_ids[0] && setDetailId(s.pair_ids[0])}
        />
      )}

      {/* ─── Coverage heatmap (the why-empty context) ─────────── */}
      <CoverageHeatmap slots={allLogical} className="mt-6" />

      <SlotDetailDrawer
        slotId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  )
}

/* ─── Summary tile ─────────────────────────────────────────────── */

function SummaryTile({
  label, value, hint, tone = 'neutral',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'neutral' | 'good' | 'bad' | 'dim'
}) {
  const toneClass = {
    neutral: 'text-text',
    good:    'text-pulse',
    bad:     'text-rose',
    dim:     'text-textFaint',
  }[tone]
  return (
    <div className="bg-ink border border-line px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-textDim">{label}</div>
      <div className={cn('text-xl font-semibold mt-1 tabular-nums font-mono', toneClass)}>
        {value}
      </div>
      {hint && <div className="text-2xs text-textFaint mt-0.5">{hint}</div>}
    </div>
  )
}

/* ─── Strategy table (the main list) ───────────────────────────── */

function StrategyTable({
  rows, sortBy, sortDir, onSort, onPick,
}: {
  rows: LogicalSlot[]
  sortBy: SortBy
  sortDir: 'asc' | 'desc'
  onSort: (by: SortBy) => void
  onPick: (s: LogicalSlot) => void
}) {
  const { t } = useTranslation('app')
  const sortArrow = (b: SortBy) => sortBy === b ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''
  return (
    <div className="bg-ink border border-line overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-textDim text-2xs uppercase tracking-wider border-b border-line bg-elevated/40">
          <tr>
            <th
              onClick={() => onSort('signal')}
              className="text-left px-3 py-2 cursor-pointer hover:text-text"
            >
              {t('slots.col.slot', { defaultValue: 'Слот' })}{sortArrow('signal')}
            </th>
            <th className="text-left px-3 py-2">
              {t('slots.col.strategy', { defaultValue: 'Стратегия' })}
            </th>
            <th
              onClick={() => onSort('pnl_24h')}
              className="text-right px-3 py-2 cursor-pointer hover:text-text whitespace-nowrap"
              title="Реальная сумма realized_pnl_pct по всем закрытым трейдам за 24ч"
            >
              {t('slots.col.live_24h', { defaultValue: 'PnL · 24ч' })}{sortArrow('pnl_24h')}
            </th>
            <th
              onClick={() => onSort('pnl_7d')}
              className="text-right px-3 py-2 cursor-pointer hover:text-text whitespace-nowrap"
              title="Реальная сумма realized_pnl_pct по всем закрытым трейдам за 7д"
            >
              {t('slots.col.live_7d', { defaultValue: 'PnL · 7д' })}{sortArrow('pnl_7d')}
            </th>
            <th
              onClick={() => onSort('live_wr')}
              className="text-right px-3 py-2 cursor-pointer hover:text-text whitespace-nowrap"
              title="Backtest win-rate"
            >
              {t('slots.col.wr', { defaultValue: 'WR' })}{sortArrow('live_wr')}
            </th>
            <th
              onClick={() => onSort('bt_compound')}
              className="text-right px-3 py-2 cursor-pointer hover:text-text whitespace-nowrap"
              title={
                'Симуляция компаунда на $100 за 30 дней бэктеста.\n' +
                'Считается как $100 × (1 + avg_pnl_per_trade × position_size/100)^trades.\n' +
                'Высокочастотные стратегии (>1000 trades/30d) дают раздутые числа\n' +
                'из-за compound × wick-fill bias (см. PULSE-63).\n' +
                'Реальность обычно в 2-3 раза ниже.'
              }
            >
              {t('slots.col.bt_compound', { defaultValue: 'BT · $100→' })}{sortArrow('bt_compound')}
            </th>
            <th
              onClick={() => onSort('rank')}
              className="text-right px-3 py-2 cursor-pointer hover:text-text whitespace-nowrap"
              title="Discovery rank (1 = best in slot)"
            >
              {t('slots.col.rank', { defaultValue: 'Rank' })}{sortArrow('rank')}
            </th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((s) => (
            <StrategyRow key={s.key} s={s} onPick={onPick} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StrategyRow({
  s, onPick,
}: {
  s: LogicalSlot
  onPick: (s: LogicalSlot) => void
}) {
  const { t } = useTranslation('app')
  const dirClass = s.direction === 'long'
    ? 'text-pulse'
    : s.direction === 'short' ? 'text-rose' : 'text-sky'
  const regimeBadge = s.regime_bucket
    ? t(`dashboard.market.phase.${s.regime_bucket}`, { defaultValue: s.regime_bucket })
    : '—'
  return (
    <tr
      onClick={() => onPick(s)}
      className={cn(
        'border-b border-line/30 hover:bg-elevated/40 cursor-pointer',
        !s.is_active && 'opacity-50',
      )}
    >
      {/* Slot identity */}
      <td className="px-3 py-2.5 align-top">
        <div className="flex items-center gap-2">
          <span className={cn(
            'inline-block w-2 h-2 rounded-full',
            stateDot(s.slot_state),
          )} />
          <span className="text-text font-semibold">
            {t(`signal_type.${s.signal_type}`, { defaultValue: s.signal_type })}
          </span>
          <span className="text-textDim">·</span>
          <span className="text-textDim">{s.timeframe}</span>
          <span className="text-textDim">·</span>
          <span className={cn('uppercase font-semibold text-2xs', dirClass)}>
            {s.direction === 'long' ? 'L' : s.direction === 'short' ? 'S' : (s.direction ?? '—')}
          </span>
        </div>
        <div className="mt-1 text-2xs text-textFaint">
          {regimeBadge}
          <span className="mx-1.5">·</span>
          <span className={stateTextClass(s.slot_state)}>
            {t(`slots.state.${s.slot_state ?? 'unknown'}`, { defaultValue: s.slot_state ?? '—' })}
          </span>
          <span className="mx-1.5">·</span>
          <span title="Слот реплицирован на N аккаунтов">×{s.user_count}</span>
        </div>
      </td>

      {/* Strategy name + A/B IDs */}
      <td className="px-3 py-2.5 align-top">
        {s.strategy_id ? (
          <>
            <div className="text-2xs text-textFaint mb-0.5">A #{s.strategy_id}</div>
            <div className="text-xs text-text truncate max-w-[260px]" title={s.strategy_name ?? ''}>
              {s.strategy_name ?? '—'}
            </div>
            {s.strategy_tp_pct != null && (
              <div className="text-2xs text-textDim mt-0.5 tabular-nums">
                TP {s.strategy_tp_pct}% · SL {s.strategy_sl_pct}% · {s.strategy_hold_min}m
              </div>
            )}
            {s.strategy_b_id && s.strategy_b_id !== s.strategy_a_id && (
              <div className="text-2xs text-sky mt-0.5">
                ⇆ B #{s.strategy_b_id} (теневая)
              </div>
            )}
          </>
        ) : (
          <span className="text-textFaint italic">{t('slots.no_strategy')}</span>
        )}
      </td>

      {/* Live PnL 24h — sum of realized_pnl_pct, exact analog of
          old system's `real_pnl_24h`. No academic backtest here. */}
      <td className="px-3 py-2.5 text-right align-top">
        {s.trades_24h > 0 ? (
          <>
            <div className={cn('tabular-nums', s.pnl_24h >= 0 ? 'text-pulse' : 'text-rose')}>
              {formatUsd(s.pnl_24h, { sign: true })}
            </div>
            <div className="text-2xs text-textFaint tabular-nums">{s.trades_24h}t</div>
          </>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>

      {/* Live PnL 7d */}
      <td className="px-3 py-2.5 text-right align-top">
        {s.trades_7d > 0 ? (
          <>
            <div className={cn('tabular-nums', s.pnl_7d >= 0 ? 'text-pulse' : 'text-rose')}>
              {formatUsd(s.pnl_7d, { sign: true })}
            </div>
            <div className="text-2xs text-textFaint tabular-nums">{s.trades_7d}t</div>
          </>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>

      {/* WR (still backtest-driven for now; live WR will need slot
          health snapshot table like the old system had). */}
      <td className="px-3 py-2.5 text-right align-top text-textDim tabular-nums">
        {s.backtest_wr != null
          ? `${(s.backtest_wr > 1 ? s.backtest_wr : s.backtest_wr * 100).toFixed(0)}%`
          : '—'}
      </td>

      {/* BT compound: "$100 → $X" using the REALISTIC cycle model
          (cycles = period_hours / avg_hold / MAX_CONCURRENT=10).
          Naive `1822 trades sequential compound` was producing
          $5143 fantasies because trades run in PARALLEL across
          symbols — you can't compound LABUSDT PnL into RAVEUSDT
          trade that opens the same minute. */}
      <td className="px-3 py-2.5 text-right align-top tabular-nums">
        {s.bt_compound_30d_pct == null ? (
          <span className="text-textFaint">—</span>
        ) : (
          <>
            <div className={cn(
              s.bt_compound_30d_pct < 0   ? 'text-rose' :
              s.bt_compound_30d_pct < 20  ? 'text-pulse' :
              s.bt_compound_30d_pct < 100 ? 'text-amber' :
                                            'text-rose/60',
            )}>
              ${(100 + s.bt_compound_30d_pct).toFixed(2)}
            </div>
            <div
              className="text-2xs text-textFaint tabular-nums"
              title={`compound на ${s.bt_compound_cycles ?? '?'} последовательных циклов (period ÷ avg_hold ÷ max_concurrent=10). Backtest trades всего ${s.bt_trades_30d ?? '?'}.`}
            >
              {s.bt_compound_30d_pct >= 0 ? '+' : ''}
              {s.bt_compound_30d_pct.toFixed(1)}%
              {s.bt_compound_cycles != null && ` · ${s.bt_compound_cycles}c`}
            </div>
          </>
        )}
      </td>

      {/* Discovery rank */}
      <td className="px-3 py-2.5 text-right align-top">
        {s.discovery_rank != null ? (
          <>
            <span className={cn(
              'pill text-2xs',
              s.discovery_rank === 1 ? 'bg-pulse-faint text-pulse' : 'bg-elevated text-textDim',
            )}>
              #{s.discovery_rank}
            </span>
            {s.discovery_run_id && (
              <div className="text-2xs text-textFaint mt-0.5">
                run #{s.discovery_run_id}
              </div>
            )}
          </>
        ) : (
          <span className="text-textFaint text-2xs">—</span>
        )}
      </td>
    </tr>
  )
}

/* ─── Coverage heatmap ─────────────────────────────────────────── */

function CoverageHeatmap({
  slots, className,
}: {
  slots: LogicalSlot[]
  className?: string
}) {
  const { t } = useTranslation('app')
  // Build: signal_type → tf → direction → regime → bool
  const cov = useMemo(() => {
    const out: Record<string, Record<string, Record<string, Set<string>>>> = {}
    for (const s of slots) {
      if (!ML_ENABLED && s.direction === 'ml') continue
      const sig = s.signal_type
      const tf = s.timeframe
      const rg = s.regime_bucket ?? 'consolidation'
      const dir = s.direction ?? 'unknown'
      out[sig] ??= {}
      out[sig][tf] ??= {}
      out[sig][tf][rg] ??= new Set()
      out[sig][tf][rg].add(dir)
    }
    return out
  }, [slots])

  const dirs = ML_ENABLED ? ['long', 'short', 'ml'] : ['long', 'short']
  const totalCells = SIGNAL_TYPES.length * TFS.length * dirs.length * REGIMES.length
  const filledCells = slots.length

  return (
    <section className={cn('bg-ink border border-line', className)}>
      <header className="px-4 py-3 border-b border-line flex items-center gap-3 flex-wrap">
        <h3 className="text-xs uppercase tracking-wider text-textDim">
          {t('slots.coverage.title', { defaultValue: 'Карта покрытия discovery' })}
        </h3>
        <span className="font-mono text-xs text-pulse">
          {filledCells} / {totalCells}
        </span>
        <p className="text-2xs text-textFaint w-full mt-1">
          {t('slots.coverage.hint', {
            defaultValue: 'Каждая ячейка — (сигнал × TF × фаза × направление). Зелёная = discovery нашёл прибыльную стратегию. Серая = ничего не нашёл (нет edge или мало данных).',
          })}
        </p>
      </header>
      <div className="p-4 overflow-x-auto">
        <table className="font-mono border-collapse">
          <thead>
            <tr>
              <th rowSpan={2} className="text-2xs text-textFaint text-right pr-3 align-bottom"></th>
              <th rowSpan={2} className="text-2xs text-textFaint text-right pr-3 align-bottom">TF</th>
              <th rowSpan={2} className="text-2xs text-textFaint text-right pr-3 align-bottom">Dir</th>
              <th colSpan={REGIMES.length} className="text-2xs text-textFaint pb-1 text-center">
                {t('slots.coverage.regimes', { defaultValue: 'фаза рынка' })}
              </th>
            </tr>
            <tr>
              {REGIMES.map((rg) => (
                <th key={rg} className="text-2xs text-textFaint px-2 pb-1 font-normal min-w-[80px] text-center">
                  {t(`dashboard.market.phase.${rg}`, { defaultValue: rg })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SIGNAL_TYPES.flatMap((sig) =>
              TFS.flatMap((tf, tfIdx) =>
                dirs.map((d, dirIdx) => {
                  const isFirstSigRow = tfIdx === 0 && dirIdx === 0
                  const isFirstTfRow  = dirIdx === 0
                  return (
                    <tr
                      key={`${sig}-${tf}-${d}`}
                      className={cn(
                        'group',
                        isFirstSigRow && 'border-t border-line',
                      )}
                    >
                      <td className={cn(
                        'text-2xs text-text pr-3 py-0.5 whitespace-nowrap',
                        !isFirstSigRow && 'text-transparent',
                      )}>
                        {isFirstSigRow
                          ? t(`signal_type.${sig}`, { defaultValue: sig })
                          : sig /* keeps width */}
                      </td>
                      <td className={cn(
                        'text-2xs text-textDim text-right pr-3 py-0.5',
                        !isFirstTfRow && 'text-transparent',
                      )}>
                        {isFirstTfRow ? tf : tf}
                      </td>
                      <td className={cn(
                        'text-2xs text-right pr-3 py-0.5 uppercase',
                        d === 'long' ? 'text-pulse' : d === 'short' ? 'text-rose' : 'text-sky',
                      )}>
                        {d === 'long' ? 'L' : d === 'short' ? 'S' : 'ML'}
                      </td>
                      {REGIMES.map((rg) => {
                        const has = cov[sig]?.[tf]?.[rg]?.has(d) ?? false
                        return (
                          <td key={rg} className="px-1 py-0.5">
                            <div
                              className={cn(
                                'h-5 rounded-sm flex items-center justify-center text-2xs font-mono',
                                has
                                  ? 'bg-pulse/25 border border-pulse/60 text-pulse'
                                  : 'bg-elevated/40 border border-line/40 text-textFaint',
                              )}
                              title={`${sig} · ${tf} · ${d} · ${rg}: ${has ? 'есть стратегия' : 'не покрыто'}`}
                            >
                              {has ? '✓' : '·'}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  )
                }),
              ),
            )}
          </tbody>
        </table>
        <div className="mt-3 flex items-center gap-5 text-2xs text-textFaint">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-pulse/25 border border-pulse/60 rounded-sm" />
            {t('slots.coverage.has', { defaultValue: 'есть стратегия' })}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-elevated/40 border border-line/40 rounded-sm" />
            {t('slots.coverage.empty', { defaultValue: 'не покрыто' })}
          </span>
        </div>
      </div>
    </section>
  )
}

/* ─── Detail drawer (mostly unchanged from rev-2) ──────────────── */

function SlotDetailDrawer({
  slotId, onClose,
}: {
  slotId: number | null
  onClose: () => void
}) {
  const { t } = useTranslation('app')
  const { data, isLoading } = useSlotDetail(slotId)

  return (
    <Drawer
      open={slotId != null}
      onClose={onClose}
      title={data ? data.slot.name : `#${slotId ?? ''}`}
      subtitle={data
        ? `${data.slot.signal_type} · ${data.slot.timeframe} · ${data.slot.direction ?? '—'} · ${data.slot.regime_bucket ?? '—'}`
        : undefined}
      widthClassName="w-[560px]"
    >
      {isLoading || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : (
        <div className="px-5 py-4 space-y-6 font-mono">
          <Section title={t('slots.section.current_strategy')}>
            <Row label="ID" value={data.slot.strategy_id ? `#${data.slot.strategy_id}` : '—'} />
            <Row label={t('slots.row.name')} value={data.slot.strategy_name ?? '—'} />
            <Row label="TP / SL / Hold"
              value={data.slot.strategy_tp_pct != null
                ? `${data.slot.strategy_tp_pct}% / ${data.slot.strategy_sl_pct}% / ${data.slot.strategy_hold_min}m`
                : '—'} />
            <Row label={t('slots.row.is_active')}
                 value={data.slot.strategy_is_active ? '✓' : '×'} />
          </Section>

          {data.slot.probing_strategy_id && (
            <Section title={t('slots.section.probing')}>
              <Row label="ID" value={`#${data.slot.probing_strategy_id}`} valueClass="text-amber" />
              <Row label={t('slots.row.name')} value={data.slot.probing_strategy_name ?? '—'} />
              <Row label={t('slots.row.started')}
                value={data.slot.probing_started_at ? timeAgo(data.slot.probing_started_at) : '—'} />
            </Section>
          )}

          <Section title={t('slots.section.health')}>
            <Row label={t('slots.row.state')} value={data.slot.slot_state ?? '—'}
              valueClass={stateTextClass(data.slot.slot_state)} />
            <Row label={t('slots.row.weak_cycles')} value={String(data.slot.weak_cycles)} />
            <Row label={t('slots.row.rework_count')} value={String(data.slot.rework_count)} />
            <Row label={t('slots.row.total_switches')} value={String(data.slot.total_switches)} />
            {data.slot.deactivation_reason && (
              <Row label={t('slots.row.deactivation_reason')} value={data.slot.deactivation_reason} />
            )}
            <Row label={t('slots.row.real_pnl_24h')}
              value={data.slot.real_pnl_24h != null
                ? `${formatUsd(data.slot.real_pnl_24h, { sign: true })} · ${data.slot.real_trades_24h}t`
                : '—'}
              valueClass={data.slot.real_pnl_24h == null
                ? 'text-textFaint'
                : data.slot.real_pnl_24h >= 0 ? 'text-pulse' : 'text-rose'} />
          </Section>

          <Section title={t('slots.section.state_history')}>
            {data.transitions.length === 0 ? (
              <p className="px-3 py-3 text-xs text-textFaint">{t('slots.no_transitions')}</p>
            ) : (
              <ul className="divide-y divide-line/40">
                {data.transitions.map((tr, i) => (
                  <li key={i} className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-textFaint tabular-nums">
                        {new Date(tr.ts).toLocaleString()}
                      </span>
                      <span className="ml-auto">
                        <span className="text-textDim">{tr.from_state}</span>
                        <span className="mx-1">→</span>
                        <span className={stateTextClass(tr.to_state)}>{tr.to_state}</span>
                      </span>
                    </div>
                    {tr.reason && (
                      <div className="mt-1 text-textDim break-words">{tr.reason}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </Drawer>
  )
}

/* ─── helpers ───────────────────────────────────────────────────── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-textFaint text-xs uppercase tracking-wider mb-2">{title}</h3>
      <div className="divide-y divide-line/40 border border-line bg-ink">{children}</div>
    </section>
  )
}

function Row({
  label, value, valueClass,
}: {
  label: string; value: string; valueClass?: string
}) {
  return (
    <div className="flex items-center px-3 py-2 text-xs">
      <span className="text-textDim">{label}</span>
      <span className={cn('ml-auto tabular-nums', valueClass ?? 'text-text')}>{value}</span>
    </div>
  )
}

function pnlTone(v: number | null): string {
  if (v == null) return 'text-textFaint'
  return v >= 0 ? 'text-pulse' : 'text-rose'
}

/** Live win-rate derived from realized trades. Mirrors what the old
 *  system showed in `real_wr_24h` — no academic backtest involved. */
function _liveWr(s: LogicalSlot): number {
  // Approximation: when trades_7d is large we trust 7d; else fall back
  // to backtest_wr (already in [0..1] post PULSE-61 migration).
  // We keep this synchronous and cheap; richer per-cycle win-rate
  // would need an additional API query.
  return s.backtest_wr ?? 0
}

function stateDot(s: string | null): string {
  switch (s) {
    case 'active':      return 'bg-pulse'
    case 'probing':     return 'bg-sky'
    case 'weak':        return 'bg-amber'
    case 'reworking':   return 'bg-amber'
    case 'disabled':    return 'bg-rose'
    case 'hibernating': return 'bg-textFaint'
    default:            return 'bg-textFaint'
  }
}

function stateTextClass(s: string | null | undefined): string {
  switch (s) {
    case 'active':      return 'text-pulse'
    case 'probing':     return 'text-sky'
    case 'weak':        return 'text-amber'
    case 'reworking':   return 'text-amber'
    case 'disabled':    return 'text-rose'
    case 'hibernating': return 'text-textFaint'
    default:            return 'text-textDim'
  }
}
